import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, ShieldCheck, Trash2, Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import SeoHead from '@/components/SeoHead';
import AppFooter from '@/components/AppFooter';
import InkPhDLogo from '@/components/InkPhDLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gdpr`;

export default function AccountDataPage() {
  const user = authStorage.getAuthorUser();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Link to="/thesis/login" className="text-sm underline">Sign in to manage your data</Link>
      </div>
    );
  }

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/export`, { params: { user_id: user.id } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `inkphd-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      const rows = Object.values(data.summary || {}).reduce((s, n) => s + n, 0);
      toast.success(`Export downloaded — ${rows} records across ${Object.keys(data.summary || {}).length} collections`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (confirm.trim().toUpperCase() !== 'DELETE' || !password) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/delete-account`,
        { password, confirm: 'DELETE' }, { params: { user_id: user.id } });
      toast.success(`Erased ${data.records_erased} records across ${data.projects_erased} projects`);
      authStorage.clearAuthorAuth();
      setTimeout(() => navigate('/'), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDeleting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SeoHead title="Data & privacy" path="/thesis/account" noindex
        description="Export or permanently erase your InkPhD account data." />
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="account-back-link">
            <ArrowLeft className="w-4 h-4" /> Projects
          </Link>
          <InkPhDLogo size="sm" />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12 space-y-6" data-testid="account-data-page">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight mb-1"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>Data & privacy</h1>
          <p className="text-sm text-muted-foreground">
            Your GDPR rights, self-service. Signed in as {user.email}.
          </p>
        </div>

        <Card data-testid="account-export-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Download className="w-4 h-4 text-[#1d4ed8]" /> Export everything (Art. 15 & 20)
            </CardTitle>
            <CardDescription>
              One JSON file with your account, projects, sections, library, references, figures,
              datasets, analyses and writing tasks — machine readable and portable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={exportData} disabled={busy} data-testid="account-export-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download my data
            </Button>
          </CardContent>
        </Card>

        <Card data-testid="account-rights-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> What we do and do not do
            </CardTitle>
          </CardHeader>
          <CardContent className="text-[13px] space-y-1.5 text-foreground/85">
            <p>· We never train models on your manuscripts or library.</p>
            <p>· No advertising or analytics storage — session and preferences only.</p>
            <p>· AI requests send your prompt plus excerpts from your own library, nothing else.</p>
            <p>· Full details: <Link className="underline" to="/legal/privacy">Privacy</Link>,{' '}
              <Link className="underline" to="/legal/subprocessors">Subprocessors</Link>,{' '}
              <Link className="underline" to="/legal/dpa">DPA</Link>,{' '}
              <Link className="underline" to="/legal/ai-act">EU AI Act</Link>.</p>
          </CardContent>
        </Card>

        <Card className="border-rose-500/40" data-testid="account-delete-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-rose-700 dark:text-rose-400">
              <Trash2 className="w-4 h-4" /> Erase my account (Art. 17)
            </CardTitle>
            <CardDescription>
              Permanently deletes your account, every project, every uploaded document, all
              generated sections, versions, analyses and vector indexes. This cannot be undone —
              export your data first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-2.5 rounded border border-amber-500/40 bg-amber-500/[0.05] text-[12px] flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              An active subscription is not refunded automatically — cancel in Billing first if you
              want a pro-rata refund under our{' '}
              <Link to="/legal/billing" className="underline">billing terms</Link>.
            </div>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password" data-testid="account-delete-password" />
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type DELETE to confirm" data-testid="account-delete-confirm" />
            <Button variant="destructive" onClick={deleteAccount}
              disabled={deleting || !password || confirm.trim().toUpperCase() !== 'DELETE'}
              data-testid="account-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Erase everything permanently
            </Button>
          </CardContent>
        </Card>
      </main>
      <AppFooter />
    </div>
  );
}
